// src/marketing/channels/hooks/useBudgetLines.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import type { BudgetLine } from '../../../lib/types'

const LINES_STALE = 1000 * 60 * 2

const lineKeys = {
  all: ['budget-lines'] as const,
  list: (appId: string) => ['budget-lines', 'list', appId] as const,
}

export function useBudgetLines(appId: string | null) {
  return useQuery<BudgetLine[]>({
    queryKey: appId ? lineKeys.list(appId) : lineKeys.all,
    queryFn: () =>
      api
        .get<{ lines: BudgetLine[] }>(`/api/budget-lines?appId=${appId}`)
        .then((r) => r.lines),
    enabled: appId !== null,
    staleTime: LINES_STALE,
  })
}

export function useCreateBudgetLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<BudgetLine> & { appId: string; category: string }) =>
      api.post<{ line: BudgetLine }>('/api/budget-lines', body).then((r) => r.line),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lineKeys.all }),
  })
}

export function useUpdateBudgetLine(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<BudgetLine>) =>
      api.put<{ line: BudgetLine }>(`/api/budget-lines/${id}`, body).then((r) => r.line),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lineKeys.all }),
  })
}

export function useDeleteBudgetLine() {
  const qc = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id: string) =>
      api.del<{ success: boolean }>(`/api/budget-lines/${id}`).then(() => undefined),
    onSuccess: () => void qc.invalidateQueries({ queryKey: lineKeys.all }),
  })
}
